/**
 * **A purchase order, printed** (James, Oct 1 — A9; Print.dc.html `po`, Oct 14). Money had no print for a PO: the number, the
 * vendor, what it is for, the lines and the total, and who approved it — one letter page, for a
 * vendor who wants paper. The order is the API's; nothing is computed here but the line totals
 * the order already carries.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatDate, formatMoney } from '@cider/ui';
import { useLoad, type Order, type Vendor } from '../views/money/shared.js';

export function OrderPage({ id }: { id: string }): ReactNode {
  const order = useLoad<Order>(`/money/orders/${id}`);
  const vendors = useLoad<{ vendors: Vendor[] }>('/money/vendors');
  const o = order.data;
  if (order.error) return <p role="alert">{order.error}</p>;
  if (!o) return <p>Looking…</p>;
  const vendor = vendors.data?.vendors.find((v) => v.id === o.vendorId)?.name ?? 'the vendor';
  return (
    <PrintRoot
      kind="letter"
      title={`Purchase order ${o.number ?? ''}`}
      pages={[
        <Paper
          key="1"
          masthead={o.number ? `Purchase order ${o.number}` : 'Not yet an order — no number'}
          submast={`Saint Lawrence Apple Fest · to ${vendor}`}
          stamp={formatDate(o.sentAt ?? o.createdAt, { year: true }).toUpperCase()}
          rows={[
            ...o.lines.map((l, i) => ({
              key: String(i),
              a: `${l.quantity === 1 ? '' : `${String(l.quantity)} × `}${l.description}`,
              b: l.quantity === 1 ? undefined : `${formatMoney(l.unitCents)} each`,
              c: formatMoney(l.quantity * l.unitCents),
            })),
            { key: 'total', a: 'Total', c: formatMoney(o.totalCents) },
            { key: 'for', a: 'What it is for', b: o.what },
          ]}
          foot={`Please put ${o.number ? `order ${o.number}` : 'the order number'} on your invoice. Send it to Saint Lawrence Apple Fest, 44633 Utica Rd, Utica, MI 48317.`}
        />,
      ]}
    />
  );
}
